import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';

const formatDate = (d) => new Date(d).toLocaleString();
const formatDateShort = (d) => new Date(d).toLocaleDateString();

export default function AIConversationHistory() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get('/ai-agent/conversations?limit=20');
      setConversations(data.conversations);
      setNextToken(data.nextToken || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await axiosInstance.get(`/ai-agent/conversations?limit=20&cursor=${encodeURIComponent(nextToken)}`);
      setConversations((prev) => [...prev, ...data.conversations]);
      setNextToken(data.nextToken || null);
    } catch {
      // leave nextToken as-is so the button just retries on next click
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const openConversation = async (id) => {
    if (selectedId === id) { setSelectedId(null); setMessages([]); return; }
    setSelectedId(id);
    setMsgLoading(true);
    try {
      const { data } = await axiosInstance.get(`/ai-agent/conversations/${id}`);
      setMessages(data.messages);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const handleExport = async (id, title, e) => {
    e.stopPropagation();
    try {
      const { data } = await axiosInstance.get(`/ai-agent/conversations/${id}/export`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Export failed');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await axiosInstance.delete(`/ai-agent/conversations/${id}`);
      setConversations((prev) => prev.filter((c) => c._id !== id));
      if (selectedId === id) { setSelectedId(null); setMessages([]); }
    } catch {
      alert('Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">AI Conversation History</h1>
            <p className="text-xs text-gray-500">Your past conversations with the AI agent</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500" />
          </div>
        ) : error ? (
          <div className="text-center py-20 text-red-500">{error}</div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <svg className="mx-auto mb-4 w-16 h-16 text-gray-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
            </svg>
            <p className="text-lg font-medium">No conversations yet</p>
            <p className="text-sm mt-1">Start chatting with the AI agent to see your history here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <div key={conv._id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Conversation row */}
                <div
                  onClick={() => openConversation(conv._id)}
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">{conv.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDateShort(conv.createdAt)} &middot; {conv.messageCount} messages &middot; {conv.mode}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <button
                      onClick={(e) => handleExport(conv._id, conv.title, e)}
                      className="text-xs px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition font-medium"
                    >
                      Export
                    </button>
                    <button
                      onClick={(e) => handleDelete(conv._id, e)}
                      disabled={deleting === conv._id}
                      className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition font-medium disabled:opacity-50"
                    >
                      {deleting === conv._id ? '…' : 'Delete'}
                    </button>
                    <svg
                      xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className={`text-gray-400 transition-transform ${selectedId === conv._id ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>

                {/* Messages panel */}
                {selectedId === conv._id && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 max-h-96 overflow-y-auto">
                    {msgLoading ? (
                      <div className="flex justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500" />
                      </div>
                    ) : messages.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No messages</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg._id}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                              msg.role === 'user'
                                ? 'bg-blue-600 text-white rounded-br-sm'
                                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                            }`}>
                              <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                                {formatDate(msg.createdAt)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Pagination */}
            {nextToken && (
              <div className="flex justify-center pt-4">
                <button
                  disabled={loadingMore}
                  onClick={loadMore}
                  className="px-4 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40 transition"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
